import { TViewDefinition } from "./view-resolver-utils";
import { IQueryItem } from "../components/masthead/masthead.component.interface";
import { IConfig } from "../hooks";
import { generateCacheKey, getCachedData, setCachedData } from "./common-utils";
import { ITypeAheadSearchItem } from "../components/masthead/masthead.component";
import { SEARCH_COPY_MAP } from "../constants/masthead-constants";

interface IFetchTypeAheadSearchParams {
  value: string;
  config: IConfig | null;
  locale: string;
  cartId?: string | null;
  snowplowSpecifications?: {
    domainUserId?: string;
  };
  ciamAccessToken?: string;
  customerId?: string | null;
  setQueryList: React.Dispatch<React.SetStateAction<IQueryItem[]>>;
  onComplete?: () => void;
}

export const fetchTypeAheadSearch = async ({
  value,
  config,
  locale,
  cartId,
  snowplowSpecifications,
  ciamAccessToken,
  customerId,
  setQueryList,
  onComplete,
}: IFetchTypeAheadSearchParams) => {
  const { hostname } = window.location;
  const isPharmaprix =
    hostname.includes("pharmaprix") || hostname.includes("phx");

  const isLoggedIn = Boolean(ciamAccessToken && customerId);

  const options: RequestInit = {
    headers: {
      "X-Loblaw-Banner-ID": isPharmaprix ? "pharmaprix" : "shoppers",
      "X-Loblaw-Client-Key": "SDM_WEB",
      "Accept-Language": locale,
      "X-Loblaw-Tenant-ID": "SHOPPERS_DRUG_MART",
      "Content-Type": "application/json",
      ...(isLoggedIn && { authorization: `Bearer ${ciamAccessToken}` }),
    },
    body: JSON.stringify({
      search: {
        term: value,
        store_id: isPharmaprix ? "4099p" : "4099s",
        size: 7,
        display_category_filters: true,
        cart_id: cartId,
        snowplow_specifications: {
          domain_user_id: snowplowSpecifications?.domainUserId,
        },
      },
    }),
    method: "POST",
    redirect: "follow",
  };

  try {
    const response = await fetch(
      `${config?.heliosBaseUrl}/search/type-ahead`,
      options
    );
    const result = await response.json();
    if (result?.search?.type_ahead_data) {
      // Mapping the results to typeahead component
      const formattedData = result.search.type_ahead_data.map(
        ({ suggestion }: { suggestion: string }) => ({
          displayText: suggestion,
          plainText: suggestion,
        })
      );
      setQueryList(formattedData);
    } else {
      setQueryList([]);
    }
  } catch {
    // Typeahead fetch failed, show empty results
    setQueryList([]);
  } finally {
    onComplete?.();
  }
};

export const fetchColdStartSearch = async ({
  config,
  locale,
}: any): Promise<TViewDefinition> => {
  // Generate cache key based on locale and banner
  const cacheKey = generateCacheKey(locale);

  // Try to get data from cache first
  const cachedData = getCachedData(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  let responseData = null;
  const { hostname } = window.location;
  const isPharmaprix =
    hostname.includes("pharmaprix") || hostname.includes("phx");
  const banner = isPharmaprix ? "pharmaprix" : "shoppersdrugmart";

  const options: RequestInit = {
    headers: {
      "X-Loblaw-Banner-ID": isPharmaprix ? "pharmaprix" : "shoppers",
      "X-Loblaw-Client-Key": "SDM_WEB",
      "Accept-Language": locale,
      "X-Loblaw-Tenant-ID": "SHOPPERS_DRUG_MART",
      "Content-Type": "application/json",
      "x-apikey": config?.apiKey,
      "x-application-type": "Web",
      authorization: "",
    },
    body: JSON.stringify({ banner }),
    method: "POST",
    redirect: "follow",
  };

  try {
    const response = await fetch(
      `${config?.sduiBaseUrl}/search-coldstart`,
      options
    );
    const result = await response.json();
    responseData = result || [];

    // Cache the successful response
    if (responseData) {
      setCachedData(cacheKey, responseData as TViewDefinition);
    }
  } catch {
    // Cold start fetch failed, return empty array
    responseData = [];
  }

  return responseData as unknown as TViewDefinition;
};

//used to remove section key prefix in autocomplete before sending value to search
export const trimSectionKey = (term: string) =>
  term.replace(/^(?:\w*::)/g, "").trim();

/**
 * Helper function to create loading state for typeahead search
 */
export const createLoadingState = (): ITypeAheadSearchItem[] => [
  {
    suggestion: "loading",
    isLoading: true,
    isEmptyMessage: false,
  },
];

/**
 * Helper function to create empty message state for typeahead search
 */
export const createEmptyState = (locale: string): ITypeAheadSearchItem[] => [
  {
    suggestion:
      SEARCH_COPY_MAP[locale as keyof typeof SEARCH_COPY_MAP].EMPTY_MESSAGE,
    isLoading: false,
    isEmptyMessage: true,
  },
];

/**
 * Helper function to transform query results to typeahead search items
 */
export const transformQueryResults = (
  queryList: IQueryItem[]
): ITypeAheadSearchItem[] =>
  queryList.map(({ displayText }) => ({
    suggestion: displayText,
    isLoading: false,
    isEmptyMessage: false,
  }));

/**
 * Helper function to check if search should show loading state
 */
export const shouldShowLoading = (
  searchTerm: string,
  isLoading: boolean,
  hasLoadedOnce: boolean,
  queryListLength: number,
  searchCharCount: number
): boolean =>
  searchTerm.length >= searchCharCount &&
  (isLoading || (!hasLoadedOnce && queryListLength === 0));

/**
 * Helper function to check if search should show empty state
 */
export const shouldShowEmpty = (
  searchTerm: string,
  isLoading: boolean,
  hasLoadedOnce: boolean,
  queryListLength: number,
  searchCharCount: number
): boolean =>
  !isLoading &&
  hasLoadedOnce &&
  queryListLength === 0 &&
  searchTerm.length >= searchCharCount;

/**
 * Build typeahead suggestions
 */
export const buildTypeaheadSuggestions = (
  searchTerm: string,
  isLoading: boolean,
  hasLoadedOnce: boolean,
  queryList: IQueryItem[],
  searchCharCount: number,
  locale: string
): ITypeAheadSearchItem[] => {
  if (
    shouldShowLoading(
      searchTerm,
      isLoading,
      hasLoadedOnce,
      queryList.length,
      searchCharCount
    )
  ) {
    return createLoadingState();
  }

  if (
    shouldShowEmpty(
      searchTerm,
      isLoading,
      hasLoadedOnce,
      queryList.length,
      searchCharCount
    )
  ) {
    return createEmptyState(locale);
  }

  return transformQueryResults(queryList);
};
